export type ViewportConfig = {
	autoZoom: boolean;
	zoom: number;
	minZoom: number;
	maxZoom: number;
	zoomStep: number;
};

export type ViewportState = {
	zoom: number;
	rotation: number;
};

export type ViewportActions = {
	setRotation: (rotation: number) => void;
	smoothRotation: (rotation: number) => void;
	setZoom: (zoom: number) => void;
	smoothZoom: (zoom: number) => void;
	triggerAutoZoom: () => void;
};

export class Viewport {
	#state: ViewportState;
	#config: ViewportConfig;
	#actions: ViewportActions;

	constructor (initialConfig: Partial<ViewportConfig> = {}) {
		this.#config = {
			autoZoom: true,
			zoom: 1.0,
			minZoom: 0.1,
			maxZoom: 5.0,
			zoomStep: 0.1,
			...initialConfig
		};

		this.#state = {
			zoom: this.#config.zoom,
			rotation: 0
		};

		this.#actions = {
			setRotation: (rotation: number) => this.setRotation(rotation),
			smoothRotation: (rotation: number) => this.smoothRotation(rotation),
			setZoom: (zoom: number) => this.setZoom(zoom),
			smoothZoom: (zoom: number) => this.smoothZoom(zoom),
			triggerAutoZoom: () => this.triggerAutoZoom()
		};
	}

	get state (): ViewportState {
		return { ...this.#state };
	}

	get config (): ViewportConfig {
		return { ...this.#config };
	}

	get actions (): ViewportActions {
		return this.#actions;
	}

	// Getters for HaxeComponents compatibility
	get zoom (): number {
		return this.#state.zoom;
	}

	get rotation (): number {
		return this.#state.rotation;
	}

	updateConfig (newConfig: Partial<ViewportConfig>): void {
		this.#config = {
			...this.#config,
			...newConfig
		};

		// If zoom config is updated, update the state zoom as well
		if (newConfig.zoom !== undefined) {
			this.#state.zoom = this.#config.zoom;
		}
	}

	private smoothZoom (targetZoom: number): void {
		// Clamp zoom to min/max bounds
		const clampedZoom = Math.max(
			this.#config.minZoom,
			Math.min(this.#config.maxZoom, targetZoom)
		);

		// Update state zoom
		this.#state.zoom = clampedZoom;

		// Emit zoom change event or call callback
		this.onZoomChange?.(clampedZoom);
	}

	private triggerAutoZoom (): void {
		if (this.#config.autoZoom) {
			this.onAutoZoomRequested?.();
		}
	}

	// Callback fired when auto-zoom is requested (wired to engine.performAutoZoom)
	public onAutoZoomRequested?: () => void;

	// Optional callback for zoom changes
	public onZoomChange?: (zoom: number) => void;

	// Method to set zoom (as required by the interface)
	public setZoom (zoom: number): void {
		this.smoothZoom(zoom);
	}

	private smoothRotation (targetRotation: number): void {
		this.#state.rotation = targetRotation;
		this.onRotationChange?.(targetRotation);
	}

	private onRotationChange?: (rotation: number) => void;

	public setRotation (rotation: number): void {
		this.smoothRotation(rotation);
	}
}
